/**
 * ConditionalSectionNode — a box around text, tables and fields that only
 * applies when a checkbox is ticked. On the signing page it stays covered until
 * the box is ticked; in the signed PDF it is marked "Not applicable" otherwise.
 * Fields inside follow the section's checkbox (unless they have their own).
 * Sections can be nested, e.g. "under 4 months" inside "under 6 months".
 */
import { useState } from 'react';
import { Node } from '@tiptap/core';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { liftTarget } from '@tiptap/pm/transform';
import { checkboxesIn, type CheckboxChoice } from './checkboxes';

function ConditionalSectionView({ node, editor, getPos, updateAttributes }: NodeViewProps) {
  const showIf: string | null = node.attrs.showIf ?? null;
  const range = (): [number, number] | undefined => {
    const pos = typeof getPos === 'function' ? getPos() : undefined;
    return typeof pos === 'number' ? [pos, pos + node.nodeSize] : undefined;
  };
  // Checkboxes outside this section (one inside could never be ticked while covered).
  const load = () => checkboxesIn(editor.state.doc, { excludeRange: range() });
  const [choices, setChoices] = useState<CheckboxChoice[]>(load);
  const current = choices.find((c) => c.fieldId === showIf);

  const unwrap = () => {
    const r = range();
    if (!r) return;
    editor.chain().focus().command(({ tr }) => {
      const $from = tr.doc.resolve(r[0] + 1);
      const $to = tr.doc.resolve(r[1] - 1);
      const blocks = $from.blockRange($to, (n) => n === tr.doc.nodeAt(r[0]));
      const target = blocks && liftTarget(blocks);
      if (!blocks || target == null) return false;
      tr.lift(blocks, target);
      return true;
    }).run();
  };

  return (
    <NodeViewWrapper className={`cond-section-box${showIf ? '' : ' cond-section-unset'}`}>
      <div className="cond-section-head" contentEditable={false}>
        <span className="cond-section-title">Conditional section — shown only if</span>
        <select
          aria-label="Checkbox that shows this section"
          value={showIf ?? ''}
          onMouseDown={() => setChoices(load())}
          onFocus={() => setChoices(load())}
          onChange={(e) => updateAttributes({ showIf: e.target.value || null })}
        >
          <option value="">— choose a checkbox —</option>
          {choices.map((c) => <option key={c.fieldId} value={c.fieldId}>☑ {c.name}</option>)}
          {showIf && !current && <option value={showIf}>a removed checkbox — choose again</option>}
        </select>
        <span className="cond-section-title">is ticked</span>
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={unwrap} title="Remove the box and keep its content">
          ✕ Remove section
        </button>
      </div>
      {!showIf && (
        <div className="cond-section-hint" contentEditable={false}>
          Choose the checkbox that switches this section on. It should sit just above the section.
        </div>
      )}
      <NodeViewContent className="cond-section-content" />
    </NodeViewWrapper>
  );
}

export const ConditionalSectionNode = Node.create({
  name: 'conditionalSection',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      // fieldId of the checkbox that switches this section on
      showIf: { default: null },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', { class: 'cond-section', 'data-show-if': HTMLAttributes.showIf ?? undefined }, 0];
  },

  parseHTML() {
    return [{ tag: 'div.cond-section', getAttrs: (el) => ({ showIf: (el as HTMLElement).dataset['showIf'] || null }) }];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ConditionalSectionView);
  },
});
